import Button from "@/components/forms/Button/Button";
import styles from "./advance-shipment-step-modal.module.css";
import { Modal, ModalBody, ModalFooter } from "@/components/Modal/Modal";
import { useShipmentContext } from "@/contexts/ShipmentContext";
import { FbaBatchStatus, TransportType } from "@boxem/core-api-schema";
import { getBatchStep } from "../utils";
import Flex from "@/components/Flex/Flex";
import Alert from "@/components/Alert/Alert";
import Progress from "@/components/Progress/Progress";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";

export interface AdvanceShipmentStepModalProps {
  loading: boolean;
  open: boolean;
  closeOnOutsideClick?: boolean;
  onClose: (confirmed: boolean) => void;
}

export default function AdvanceShipmentStepModal(props: AdvanceShipmentStepModalProps) {
  const { closeOnOutsideClick = true } = props;
  const { currentBatch } = useShipmentContext();

  if (!currentBatch) return null;

  const stepNumber = getBatchStep(currentBatch);

  const confirmShipmentsTemplate = (progressPercentage: number) => {
    return (
      <>
        <div className={styles.modalHeader}>
          <img src={"/images/illustration-robot-delivering-order.svg"} width={270} height={239} alt="illustration" />
        </div>
        <div className={styles.modalBody}>
          <Flex direction="column" gap={4}>
            <h3>Confirm Placement Option {currentBatch.shipmentType === TransportType.SPD && "& Accept Shipping Charges"}</h3>
            <p>
              By selecting a placement option, you determine the complete list of shipments you must complete to send your products to Amazon's fulfillment warehouse(s) {currentBatch.shipmentType === TransportType.SPD && ", including their transportation fees."}
            </p>
          </Flex>
          {currentBatch.shipmentType === TransportType.SPD && (
            <Alert
              type="warning"
              message={`If you continue, you will be accepting shipping charges, and be responsible for completing your shipments. This action is irreversible.`}
            />
          )}
          {currentBatch.shipmentType === TransportType.LTL && (
            <Alert
              type="warning"
              message={`If you continue you will be responsible for completing your shipments. This action is irreversible.`}
            />
          )}

          <p className="weak">
            {`Your request will be processed in real-time, and will likely take a few minutes.`}
          </p>
          {(!!progressPercentage || progressPercentage === 0) && <Progress progressPercentage={progressPercentage}/>}
        </div>
      </>
    )
  }

  const confirmPackingGroupsTemplate = (progressPercentage: number) => {
    return <>
      <div className={styles.modalHeader}>
        <img src={"/images/box-packed.svg"} width={270} height={239} alt="illustration" />
      </div>
      <div className={styles.modalBody}>
        <Flex direction="column" gap={4}>
          <h3>Confirm Packing Groups</h3>
          <p>
            Once all your items are packed, we can request the placement options and transportation fees for your shipment(s).
          </p>
        </Flex>
        <Alert
          type="warning"
          message="If you continue, you won't be able to go back and change details about your how your products are packed. This action is irreversible."
        />
        <p className="weak">
          {`Your request will be processed in real-time, and will likely take up to a few minutes.`}
        </p>
        {(!!progressPercentage || progressPercentage === 0) && <Progress progressPercentage={progressPercentage}/>}
      </div>
    </>
  }

  const confirmTransportTemplate = (progressPercentage: number) => {
    return (
      <>
        <div className={styles.modalHeader}>
          <img src={"/images/illustration-robot-delivering-order.svg"} width={270} height={239} alt="illustration" />
        </div>
        <div className={styles.modalBody}>
          <Flex direction="column" gap={4}>
            <h3>Confirm Transport & Accept Shipping Charges</h3>
            <p>
              By confirming transport, you determine the complete list of shipments you must complete to send your products to Amazon's fulfillment warehouse(s) including their transportation fees.
            </p>
          </Flex>
          
          <Alert
            type="warning"
            message={`If you continue, you will be accepting shipping charges, and be responsible for completing your shipments. This action is irreversible.`}
          />

          <p className="weak">
            {`Your request will be processed in real-time, and will likely take a few minutes.`}
          </p>
          {(!!progressPercentage || progressPercentage === 0) && <Progress progressPercentage={progressPercentage}/>}
        </div>
      </>
    )
  }

  return (
    <Modal
      closeIcon={!props.loading}
      closeOnOutsideClick={!props.loading && closeOnOutsideClick}
      open={props.open}
      onOpenChange={open => {
        if (!props.loading) {
          props.onClose(false);
        }
      }}
    >
      <ModalBody>
        {(() => {
          switch (stepNumber) {
            default:
            case 0: {
              let progressPercentage;
              switch (currentBatch.status) {
                case FbaBatchStatus.CHOOSE_PRODUCTS:
                  // we're in this state when we open the modal, don't set the progress percentage so we don't show the progress bar.
                  break;
                case FbaBatchStatus.CREATING_INBOUND_PLAN:
                  progressPercentage = 20;
                  break;
                case FbaBatchStatus.INBOUND_PACKING_OPTION_FETCHED:
                  progressPercentage = 30;
                  break;
                case FbaBatchStatus.CREATING_INBOUND_PLACEMENT_OPTION:
                  progressPercentage = 50; // this one is pretty long.
                  break;
                case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_CREATED:
                  progressPercentage = 80; // this one is also pretty long.
                  break;
              }
              return (
                <>
                  <div className={styles.modalHeader}>
                    <img src={"/images/search.svg"} width={270} height={239} alt="illustration" />
                  </div>
                  <div className={styles.modalBody}>
                    <Flex direction="column" gap={4}>
                      <h3>Time to send your request to Amazon</h3>
                      <p>
                        You've chosen your products and confirmed their details. Now it's time to send your shipment request to Amazon
                        for approval.
                      </p>
                    </Flex>
                    <Alert
                      type="warning"
                      message="If you continue, you won't be able to go back and add more products to this shipment."
                    />
                    <p className="weak">
                      {`Your request will be processed in real-time, and you should receive Amazon's conditions within a few minutes. Don't go anywhere!`}
                    </p>
                    {(!!progressPercentage || progressPercentage === 0) && <Progress progressPercentage={progressPercentage}/>}
                  </div>
                </>
              );
            }
   
            case 1: {
              let progressPercentage;
              switch (currentBatch.status) {
                case FbaBatchStatus.INBOUND_BOX_SUBMITTED:
                  progressPercentage = 10;
                  break;
                case FbaBatchStatus.INBOUND_BOX_PROCESSED:
                  progressPercentage = 50;
                  break;
                case FbaBatchStatus.CREATING_INBOUND_PLACEMENT_OPTION:
                  progressPercentage = 80;
                  break;
                case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_CREATED:
                  progressPercentage = 90;
                  break;
              }

              if (currentBatch.shipmentType === TransportType.LTL) {
                return confirmShipmentsTemplate(progressPercentage);
              } else {
                return confirmPackingGroupsTemplate(progressPercentage);
              }
            }
            case 2: {
              let progressPercentage;

              // in LTL, the order is:
              // INBOUND_PLACEMENT_OPTION_CONFIRMED
              // INBOUND_BOX_SUBMITTED
              // INBOUND_BOX_PROCESSED

              switch (currentBatch.status) {
                case FbaBatchStatus.CONFIRMING_BATCH:
                  progressPercentage = 10;
                  break;
                case FbaBatchStatus.BATCH_PLACEMENT_OPTION_CONFIRMED:
                  progressPercentage = 50;
                  break;
                case FbaBatchStatus.INBOUND_PLACEMENT_OPTION_CONFIRMED:
                  if (currentBatch.shipmentType === TransportType.SPD) {
                    progressPercentage = 80;
                  }
                  break;
                case FbaBatchStatus.INBOUND_BOX_SUBMITTED:
                  progressPercentage = 50;
                  break;
                case FbaBatchStatus.INBOUND_BOX_PROCESSED:
                  progressPercentage = 50;
                  break;
                case FbaBatchStatus.CONFIRMING_BATCH_TRANSPORTATION_OPTION:
                  progressPercentage = 90;
                  break;
              }

              if (currentBatch.shipmentType === TransportType.LTL) {
                return confirmPackingGroupsTemplate(progressPercentage);
              } else {
                return confirmShipmentsTemplate(progressPercentage);
              }
            }
            case 3: {
              // LTL confirm transport
              let progressPercentage;
              switch (currentBatch.status) {
                case FbaBatchStatus.BOL_FETCHED:
                  progressPercentage = 50;
                  break;

                case FbaBatchStatus.INBOUND_TRANSPORT_OPTION_CONFIRMED:
                  progressPercentage = 100;
                  break;
              }
              return confirmTransportTemplate(progressPercentage);
            }
          }
        })()}
      </ModalBody>

      <ModalFooter>
        {(() => {
          if (props.loading) return (
            <Flex justify="end" align="center" gap={2}>
              <LoadingSpinner />
            </Flex>
          );

          let cancelButtonText, confirmButtonText;
          switch (stepNumber) {
            default:
            case 0:
              cancelButtonText = "Go back and edit products";
              confirmButtonText = "Submit Products"
              break;

            case 1:
              cancelButtonText = "Go back and edit pack groups";
              confirmButtonText = currentBatch.shipmentType === TransportType.LTL 
                ? "Submit Placement" : "Submit Pack Groups"
              break;

            case 2:
              cancelButtonText = "Go back";
              confirmButtonText = currentBatch.shipmentType === TransportType.LTL ? "Generate Transportation Options" : "Confirm Placement Option & Accept Shipping Charges"
              break;

            case 3:
              // LTL confirm transportation options
              cancelButtonText = "Go back";
              confirmButtonText = "Accept Shipping Charges"
          }

          return (
            <Flex justify="end" align="center" gap={2}>
              <Button type="secondary" size="large" onClick={() => props.onClose(false)}>
                {cancelButtonText}
              </Button>
              <Button type="primary" size="large" onClick={() => props.onClose(true)}>
                {confirmButtonText}
              </Button>
            </Flex>
          );
        })()}
      </ModalFooter>
    </Modal>
  );
}
